// Companion/blocks/ChatTurnFooter.tsx
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectPendingActionProposal,
  type ChatRootState,
} from '@/slices/chat/chatSelectors';
import {
  ACTION_PROPOSAL_TTL_MS,
  actionProposalExpired,
} from '@/slices/chat/chatSlice';
import ChatActionCard from './ChatActionCard';

export interface ChatTurnFooterProps {
  conversationId: string | null;
  /** Close the drawer when a confirmed action outcome navigates away. */
  onNavigate?: () => void;
}

/**
 * The action card between the thread and the composer. It belongs to the
 * conversation rather than to a message or a turn, which is what lets it stay
 * tappable across later turns (ORA-750): the selector hands over the newest card
 * still waiting for an answer, never the active turn's, so a student who replied
 * "yes" still has the button they were replying to.
 *
 * This is also where the card's own clock lives. The backend refuses a confirm
 * more than ten minutes after the proposal, so a card left up past that is a
 * button that cannot work — one timer, keyed on the card, puts it away when its
 * window closes. The deadline is computed from when the proposal arrived rather
 * than from when this effect ran, so closing and reopening the drawer does not
 * restart it, and a card that expired while the drawer was shut goes on sight.
 */
const ChatTurnFooter = ({
  conversationId,
  onNavigate,
}: ChatTurnFooterProps) => {
  const dispatch = useDispatch();

  const proposal = useSelector((state: ChatRootState) =>
    selectPendingActionProposal(state, conversationId)
  );

  const actionId = proposal?.actionId ?? null;

  // A card that has already run is a receipt and does not go stale; one built
  // before `proposedAt` existed has no clock to read, and the server's own
  // refusal remains the backstop.
  const proposedAt =
    proposal?.status === 'pending' ? (proposal.proposedAt ?? null) : null;

  useEffect(() => {
    if (!conversationId || !actionId || proposedAt === null) return;

    const expire = () =>
      dispatch(actionProposalExpired({ conversationId, actionId }));

    const remaining = proposedAt + ACTION_PROPOSAL_TTL_MS - Date.now();

    if (remaining <= 0) {
      expire();
      return;
    }

    const timer = setTimeout(expire, remaining);

    return () => clearTimeout(timer);
  }, [actionId, conversationId, dispatch, proposedAt]);

  return proposal && conversationId ? (
    /*
     * Keyed on the action, so a different card is a different component
     * instance. The card keeps the in-flight request, the last failure and the
     * support-widget fallback in local state; without the key React reuses the
     * instance when one card replaces another — a superseded same-name proposal,
     * or an older card coming forward — and a fresh support card inherited the
     * dead end of the send that failed before it: the old error, and no button.
     */
    <ChatActionCard
      key={proposal.actionId}
      proposal={proposal}
      conversationId={conversationId}
      onNavigate={onNavigate}
    />
  ) : null;
};

export default ChatTurnFooter;
